import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { UntilDestroy } from '@ngneat/until-destroy';
import { PlanApiData, PlanId } from 'src/app/pages/new-billing/types/api.types';
import { PlanActionType, PricePeriodicity } from 'src/app/pages/new-billing/types/plan.types';
import { getPlanTierFromPlanId, planIdToPricingPlan, planTierRank } from 'src/app/pages/new-billing/utils/plan-maps';
import { UpgradeConfirmModalTrackingContext } from 'src/app/pages/new-subscribe-plans/store/model';
import { PlanStateService } from 'src/app/services/billing/plan-state.service';
import { isBasicPlanId } from 'src/app/services/company/plan-company-settings.util';
import { LocalizedPricingService } from 'src/app/services/localized-pricing/localized-pricing.service';
import { SegmentService } from 'src/app/services/segment/segment.service';
import { provideTrackingCtx } from 'src/app/services/tracking/context';
import { normalizePrice } from 'src/app/util/price-formatter.util';
import { ConcretePlan } from '../models/plan.model';

export type UpgradeBillingData = {
  currentPlan: ConcretePlan;
  onSwitchPlan: (cardPlan: ConcretePlan) => void;
  paymentRecurrence: PricePeriodicity;
  hasFixedRecurrence?: boolean;
  btnText: string;
  actionType?: PlanActionType;
  previousPlanId?: string;
  trackingContext?: UpgradeConfirmModalTrackingContext;
  footerNoteHtml?: string;
};

export type UpgradeBillingResult = {
  didChangePlan: boolean;
  action?: () => void;
};

@UntilDestroy()
@Component({
  selector: 'app-upgrade-billing-details',
  templateUrl: './upgrade-billing-details.component.html',
  styleUrls: ['./upgrade-billing-details.component.scss'],
  providers: [
    provideTrackingCtx({ location: 'billing details' }),
  ],
})
export class UpgradeBillingDetailsComponent implements OnInit {
  btnText: string;
  cardPlan: ConcretePlan;
  hasFixedRecurrence: boolean;
  isLoadingAction = false;
  errorMsg = '';

  get isCurrentPlanYearly() {
    return this.cardPlan.billingApiData.billingDetails.currentPlan.includes('_annual') ||
           this.cardPlan.billingApiData.billingDetails.currentPlan.includes('Yearly');
  }

  get confirmButtonLabelKey(): string | null {
    if (this.cardPlan.paymentModel.paymentRecurrence === 'year' && !this.isCurrentPlanYearly) {
      return 'billing.saveWithYearly';
    }
    if (this.cardPlan.paymentModel.paymentRecurrence === 'month' && this.isCurrentPlanYearly) {
      return 'billing.switchToMonthly';
    }
    return null;
  }

  get headingKey(): string {
    if (this.cardPlan.paymentModel.paymentRecurrence === 'year' && !this.isCurrentPlanYearly) {
      return 'billing.switchToYearlyBilling';
    }
    if (this.cardPlan.paymentModel.paymentRecurrence === 'month' && this.isCurrentPlanYearly) {
      return 'billing.switchToMonthlyBilling';
    }
    return this.data.actionType === 'downgrade'
      ? 'billing.confirmDowngrade'
      : 'billing.confirmUpgrade';
  }

  get isTrial(): boolean {
    return this.cardPlan.subscription.status === 'trial';
  }

  get descriptionKey(): string {
    if (!this.isTrial) {
      if (this.data.actionType === 'downgrade') {
        return 'billing.paymentSummary.downgrade.messageSwitchToMonthlyPlan';
      }
      if (this.cardPlan.paymentModel.paymentRecurrence === 'year' && !this.isCurrentPlanYearly) {
        return 'billing.paymentSummary.downgrade.messageSwitchToYearlyPlan';
      }
      return 'billing.paymentSummary.downgrade.messageDowngradeUpgradeMonthly';
    }

    return 'billing.confirmPlanChangeDescription';
  }

  get isDowngradeToBasic(): boolean {
    return this.data.actionType === 'downgrade'
      && isBasicPlanId(this.cardPlan.planId)
      && !(
        this.previousPlan?.billingPeriod === 'yearly'
        && this.selectedPlanBillingPeriod === 'monthly'
      );
  }

  get isUpgradeToStandard(): boolean {
    return this.data.actionType === 'upgrade'
      && getPlanTierFromPlanId(this.data.previousPlanId) === planTierRank.basic
      && getPlanTierFromPlanId(this.cardPlan.planId) === planTierRank.standard;
  }

  get previousPlanNameKey(): string {
    const previousPlanId = this.data.previousPlanId as PlanId;
    const pricingPlan = previousPlanId && planIdToPricingPlan[previousPlanId];
    return pricingPlan ? `billing.${pricingPlan}` : '';
  }

  get previousPlanMonthlyPrice(): number {
    const previousPlan = this.previousPlan;
    if (!previousPlan) {
      return 0;
    }

    return this.getPlanMonthlyPrice(this.getLatestMonthlyCatalogPlan(previousPlan));
  }

  get isYearlyBilling(): boolean {
    return this.cardPlan.paymentModel.paymentRecurrence === 'year';
  }

  get nextPaymentAmountLabelKey(): string {
    return this.isYearlyBilling ? 'billing.totalPerYear' : 'billing.nextPaymentAmount';
  }

  get yearlyDiscountedMonthlyPrice(): number {
    return this.planStateService.getYearlyEquivalentMonthlyPrice(this.cardPlan.paymentModel.priceMonthly);
  }

  get annualDiscountPercent(): number {
    const { totalBillingPrice, annualPayDiscount } = this.cardPlan.paymentModel;
    if (!totalBillingPrice || !annualPayDiscount) {
      return 0;
    }

    return Math.round((annualPayDiscount / totalBillingPrice) * 100);
  }

  get showYearlySavingsBanner(): boolean {
    return this.data.actionType !== 'downgrade'
      && this.isYearlyBilling
      && this.cardPlan.paymentModel.annualPayDiscount > 0;
  }

  get showYearlyDiscountLostBanner(): boolean {
    return this.cardPlan.paymentModel.paymentRecurrence === 'month' && this.isCurrentPlanYearly;
  }

  private get previousPlan(): PlanApiData | undefined {
    const previousPlanId = this.data.previousPlanId as PlanId | undefined;
    if (!previousPlanId) {
      return undefined;
    }

    return this.cardPlan.plans.find(plan => plan.planId === previousPlanId)
      ?? this.cardPlan.plans.find(plan =>
        plan.productId === previousPlanId.split('_')[0] && plan.type === 'plan',
      );
  }

  private get selectedPlanBillingPeriod(): PlanApiData['billingPeriod'] | undefined {
    return this.cardPlan.plans.find(plan => plan.planId === this.cardPlan.planId)?.billingPeriod;
  }

  private getLatestMonthlyCatalogPlan(plan: PlanApiData): PlanApiData {
    const plans = this.cardPlan.plans;
    for (let i = plans.length - 1; i >= 0; i--) {
      const candidate = plans[i];
      if (
        candidate.productId === plan.productId
        && candidate.billingPeriod === 'monthly'
        && candidate.type === 'plan'
      ) {
        return candidate;
      }
    }

    return plan;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: UpgradeBillingData,
    private dialogRef: MatDialogRef<UpgradeBillingDetailsComponent, UpgradeBillingResult>,
    private readonly localizedPricingService: LocalizedPricingService,
    private readonly planStateService: PlanStateService,
    private readonly segment: SegmentService,
  ) {
    const { currentPlan, paymentRecurrence, hasFixedRecurrence, btnText } = data;
    this.cardPlan = currentPlan;
    this.hasFixedRecurrence = hasFixedRecurrence;
    this.btnText = btnText;
    this.cardPlan.switchPlanRecurrence(paymentRecurrence);
  }

  ngOnInit(): void {
    this.trackConfirmPlanUpgradeModalViewed();
  }

  private trackConfirmPlanUpgradeModalViewed(): void {
    const { trackingContext } = this.data;
    if (!trackingContext) {
      return;
    }

    this.segment.trackInAppEvents('Confirm Plan Upgrade Modal Viewed', {
      upsellname: trackingContext.upsellname,
      linklocation: trackingContext.linklocation,
      targetPlan: trackingContext.targetPlan ?? this.cardPlan?.name,
    });
  }

  public handleCancel() {
    this.dialogRef.close({ didChangePlan: false });
  }

  public async handleSwitchPlan() {
    this.data.onSwitchPlan(this.cardPlan);
  }

  public toggleChanges(billingDiscountEvent: MatSlideToggleChange) {
    const payRecurrence = billingDiscountEvent.checked ? 'year' : 'month';
    this.cardPlan.switchPlanRecurrence(payRecurrence);
  }

  /**
   * Formats a price with localized currency based on user location and priceOverrides
   * @param price - The price to format
   * @returns string - Formatted price with appropriate currency symbol and decimals for INR
   */
  formatLocalizedPrice(price: number): string {
    const normalizedPrice = normalizePrice(price);
    const billingCurrency = this.cardPlan?.billingApiData?.billingDetails?.currency;

    // Priority 1: Use billing currency only if available and has matching price override
    if (billingCurrency && this.cardPlan) {
      // Check if cardPlan has a matching priceOverride for billing currency
      const hasMatchingOverride = this.cardPlan?.priceOverrides?.some(
        override => override.currencyCode === billingCurrency,
      );

      if (hasMatchingOverride) {
        return this.localizedPricingService.formatPriceWithCurrency(normalizedPrice, billingCurrency, 'excludePlanAndAddon');
      }
    }

    // Priority 2: If no card is added, try to get price from price overrides based on user's country
    const noCardPrice = this.localizedPricingService.getLocalizedPriceForNoCard(this.cardPlan, normalizedPrice);
    const isEnabled = this.localizedPricingService.isLocalizedPricingEnabled();
    if (isEnabled && !billingCurrency && this.cardPlan && noCardPrice) {
      return this.localizedPricingService.formatLocalizedPriceForComponent(normalizedPrice, this.cardPlan, true, 'excludePlanAndAddon');
    }

    // Priority 4: Fallback to standard formatting
    return this.localizedPricingService.formatLocalizedPriceForComponent(normalizedPrice, this.cardPlan, true, 'excludePlanAndAddon');
  }

  private getPlanMonthlyPrice(plan: PlanApiData): number {
    const billingCurrency = this.cardPlan?.billingApiData?.billingDetails?.currency;
    const override = billingCurrency
      ? plan.priceOverrides?.find(priceOverride => priceOverride.currencyCode === billingCurrency)
      : undefined;

    if (override) {
      return +override.unitPrice / 100;
    }

    return +plan.unitPrice;
  }
}
